import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from "flarum/forum/app";
import Component from "flarum/common/Component";
import Button from "flarum/common/components/Button";
import ChatMessage from "./ChatMessage";
import { MessageStreamSkeleton } from "./Skeletons";
import iconLabel from "../utils/iconLabel";
/**
 * A channel's pinned messages.
 *
 * Shares the right-hand panel geometry with the thread panel — the two are never
 * open at once. Pinning and unpinning happen on the message row itself, so there is
 * one place that does it rather than two that can disagree.
 */
export default class PinnedPanel extends Component {
  constructor() {
    super(...arguments);
    _defineProperty(this, "messages", []);
    _defineProperty(this, "loading", true);
  }
  oninit(vnode) {
    super.oninit(vnode);
    this.load();
  }
  onbeforeupdate(vnode) {
    var _this$attrs;
    const previousId = (_this$attrs = this.attrs) == null || (_this$attrs = _this$attrs.channel) == null ? void 0 : _this$attrs.id();
    super.onbeforeupdate(vnode);
    if (previousId !== undefined && vnode.attrs.channel.id() !== previousId) {
      this.loading = true;
      this.load();
    }
  }
  view() {
    const _this$attrs2 = this.attrs,
      state = _this$attrs2.state,
      onClose = _this$attrs2.onClose;
    return m("div", {
      className: "ChatThreadPanel ChatPinnedPanel"
    }, m("div", {
      className: "ChatThreadPanel-header"
    }, m("i", {
      className: "fas fa-thumbtack",
      "aria-hidden": "true"
    }), m("span", {
      className: "ChatThreadPanel-title"
    }, app.translator.trans("ramon-chat.forum.channel.pinned_messages")), m(Button, Object.assign({
      className: "Button Button--icon Button--flat",
      icon: "fas fa-xmark"
    }, iconLabel(app.translator.trans("ramon-chat.forum.channel.close_pinned", {}, true)), {
      onclick: onClose
    }))), m("div", {
      className: "ChatThreadPanel-stream"
    }, this.loading ? MessageStreamSkeleton(3) : null, !this.loading && this.messages.length === 0 ? m("div", {
      className: "ChatBrowse-empty"
    }, app.translator.trans("ramon-chat.forum.channel.no_pinned_messages")) : null, this.messages.map(message => m(ChatMessage, {
      key: message.id(),
      message: message,
      previous: null,
      state: state,
      hideThreadIndicator: true,
      onReply: msg => this.reply(msg),
      onEdit: msg => this.edit(msg)
    }))));
  }

  /**
   * Replying to a pinned message.
   *
   * The panel had no handlers, so ChatMessage drew the reply and edit buttons from
   * the message's own capability flags — the server said yes — and clicking them
   * did nothing at all. Worse than a missing button, because it looks like the
   * feature is broken rather than absent.
   *
   * The reply is staged against the channel, not the panel: the panel has no
   * composer of its own, so it closes and hands the context to the one below. That
   * is also why the target must carry its thread scope — replying to a pinned
   * message that lives inside a thread has to land in that thread, not in the
   * channel where the reply would make no sense.
   */
  reply(message) {
    this.stage(message, (channelId, threadId) => this.attrs.state.setReplyingTo(channelId, message, threadId));
  }
  edit(message) {
    this.stage(message, (channelId, threadId) => {
      var _message$content;
      this.attrs.state.setEditing(channelId, message, threadId);
      this.attrs.state.setDraft(channelId, (_message$content = message.content()) != null ? _message$content : "", threadId);
    });
  }

  /**
   * Closes the panel, opens the right composer, and applies the action to it.
   */
  stage(message, apply) {
    var _message$threadId;
    const state = this.attrs.state;
    const channelId = Number(this.attrs.channel.id());
    const threadId = (_message$threadId = message.threadId()) != null ? _message$threadId : null;
    state.showPinned = false;
    apply(channelId, threadId);
    this.attrs.onClose();

    // A pinned reply belongs to its thread, so that thread's panel has to open —
    // otherwise the composer holding the context is not the one on screen and the
    // click appears to do nothing, which is the bug this method exists to fix.
    if (threadId !== null) {
      state.activeThreadId = threadId;

      // On the full-screen page the thread is addressed by URL, and ChatPage
      // re-reads it on every update: setting the field alone would be undone on
      // the next redraw. In the drawer there is no route to set.
      if (!this.attrs.embedded) {
        m.route.set(app.route("chat.thread", {
          id: channelId,
          threadId
        }));
        return;
      }
    }
    m.redraw();
  }
  async load() {
    try {
      const results = await app.store.find("chat-messages", {
        filter: {
          channel: Number(this.attrs.channel.id()),
          pinned: true,
          // Without this the channel filter drops thread replies, and a pinned
          // message inside a thread would silently be missing from the list.
          includeThreadReplies: true
        },
        sort: "-pinnedAt",
        page: {
          limit: 50
        }
      });
      this.messages = Array.isArray(results) ? results : [];
    } catch (_unused) {
      this.messages = [];
    } finally {
      this.loading = false;
      m.redraw();
    }
  }
}
flarum.reg.add('ramon-chat', 'forum/components/PinnedPanel', PinnedPanel);